import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Pin, PinOff, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAiChatPanelResize } from './use-ai-chat-panel-resize'
import type { ReactNode } from 'react'
import type { useAiChatPanelState } from './use-ai-chat-panel-state'
import { Button } from '@/shared/ui/button'

type Panel = ReturnType<typeof useAiChatPanelState>
type FrameProps = {
  navigationPinned?: boolean
  children: ReactNode
  page: ReactNode
  panel: Panel
  title: string
  subtitle: string
  conversationControls?: ReactNode
}

function usePanelPortal(panel: Panel) {
  const [inlineHost, setInlineHost] = useState<HTMLDivElement | null>(null)
  const [overlayHost, setOverlayHost] = useState<HTMLDivElement | null>(null)
  const [hiddenHost, setHiddenHost] = useState<HTMLDivElement | null>(null)
  const [node] = useState(() => {
    const content = document.createElement('div')
    content.className = 'flex h-full min-h-0 flex-col'

    return content
  })
  let host = hiddenHost
  if (panel.open) host = panel.pinned ? inlineHost : overlayHost
  // A stable portal target preserves the transcript, composer, and active stream.
  useLayoutEffect(() => {
    if (!host) return
    host.appendChild(node)

    return () => {
      node.remove()
    }
  }, [host, node])

  return { node, setInlineHost, setOverlayHost, setHiddenHost }
}

export function AiChatPanelFrame(props: FrameProps) {
  const { panel, page, children } = props
  const portal = usePanelPortal(panel)
  const titleId = useId()
  const { open, closeChat } = panel
  const layoutRef = useRef<HTMLDivElement>(null)
  const resize = useAiChatPanelResize({
    storageKey: panel.widthStorageKey,
    layoutRef,
    pinned: panel.pinned,
    navigationPinned: props.navigationPinned,
  })
  const handle = panel.isDesktop ? <AiChatResizeHandle resize={resize} /> : null
  useEffect(() => {
    if (!open) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== 'Escape') return
      if (
        event.target instanceof Element &&
        event.target.closest('[role="dialog"], [role="menu"]')
      )
        return
      closeChat()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, closeChat])

  return (
    <>
      <div ref={layoutRef} className="relative flex h-full min-h-0 min-w-0">
        <div className="min-h-0 min-w-0 flex-1">{page}</div>
        {panel.open && panel.pinned ? (
          <aside
            aria-labelledby={titleId}
            style={{
              width: resize.width,
              maxWidth: props.navigationPinned
                ? 'calc((100% - 20rem) / 2)'
                : undefined,
            }}
            className="relative flex h-full max-w-[50%] shrink-0 flex-col border-l bg-background shadow-lg"
          >
            {handle}
            <div
              ref={portal.setInlineHost}
              className="flex h-full min-h-0 flex-col"
            />
          </aside>
        ) : null}
        {panel.open && !panel.pinned ? (
          <aside
            aria-labelledby={titleId}
            style={panel.isDesktop ? { width: resize.width } : undefined}
            className="absolute inset-y-0 right-0 z-40 flex w-full max-w-[calc(100%-3rem)] flex-col border-l bg-background shadow-2xl max-md:max-w-full"
          >
            {handle}
            <div
              ref={portal.setOverlayHost}
              className="flex h-full min-h-0 flex-col"
            />
          </aside>
        ) : null}
      </div>
      <div hidden ref={portal.setHiddenHost} />
      {createPortal(
        <>
          <AiChatPanelHeader {...props} titleId={titleId} />
          {children}
        </>,
        portal.node,
      )}
    </>
  )
}

function AiChatResizeHandle({
  resize,
}: {
  resize: ReturnType<typeof useAiChatPanelResize>
}) {
  const { t } = useTranslation('aiChat')
  const { finish } = resize
  const finishRef = useRef(finish)
  useEffect(() => {
    finishRef.current = finish
  })
  // Closing or moving the panel unmounts the handle and ends any active drag.
  useEffect(() => () => finishRef.current(), [])

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('resize')}
      aria-valuenow={Math.round(resize.width)}
      aria-valuemin={Math.round(resize.min)}
      aria-valuemax={resize.max === null ? undefined : Math.round(resize.max)}
      tabIndex={0}
      className="absolute inset-y-0 left-0 z-10 w-2 cursor-col-resize touch-none select-none hover:bg-primary/30 focus-visible:bg-primary/40 focus-visible:outline-none"
      {...resize.handleProps}
    />
  )
}

function AiChatPanelHeader({
  panel,
  title,
  subtitle,
  conversationControls,
  titleId,
}: FrameProps & { titleId: string }) {
  const { t } = useTranslation('aiChat')

  return (
    <header className="space-y-2 border-b p-4">
      <div className="flex items-start justify-between gap-2">
        <h2 id={titleId} className="min-w-0 font-semibold break-words">
          {title}
        </h2>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={t('close')}
          onClick={panel.closeChat}
        >
          <X className="size-4" />
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{subtitle}</p>
      {conversationControls}
    </header>
  )
}

export function AiChatPanelPinButton({ panel }: { panel: Panel }) {
  const { t } = useTranslation('aiChat')
  if (!panel.isDesktop) return null

  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      aria-label={t(panel.pinned ? 'unpin' : 'pin')}
      onClick={panel.togglePin}
    >
      {panel.pinned ? (
        <PinOff className="size-4" />
      ) : (
        <Pin className="size-4" />
      )}
    </Button>
  )
}
